'use client';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';

import { cn } from '../../lib/utils';
import { Dialog, DialogClose, DialogContent, DialogSubTitle, DialogTitle } from '../ui/dialog-v2';

import { TeamLeaguePointsSummary } from './types';

const EMPTY_SUMMARY: TeamLeaguePointsSummary = {
	totalTeamPoints: 0,
	matchupPoints: 0,
	tiebreakerPoints: 0,
	matchPoints: 0,
	gamePoints: 0
};

export const DEFAULT_MATCHUP_TITLE = 'Matchup';
export const DEFAULT_TIE_BREAKER_TITLE = 'Tiebreaker';

type PointsSummaryComponent = Exclude<keyof TeamLeaguePointsSummary, 'totalTeamPoints'>;

interface PointsSummaryRow {
	key: PointsSummaryComponent;
	label: string;
	override?: boolean;
}

const buildComponentRows = (matchupTitle: string, tieBreakerTitle: string): PointsSummaryRow[] => [
	{ key: 'matchupPoints', label: `${matchupTitle} points`, override: true },
	{ key: 'tiebreakerPoints', label: `${tieBreakerTitle} points`, override: true },
	{ key: 'matchPoints', label: 'Match points' },
	{ key: 'gamePoints', label: 'Game points' }
];

interface TeamCardProps {
	title: string;
	summary: TeamLeaguePointsSummary;
	rows: PointsSummaryRow[];
}

const TeamCard = ({ title, summary, rows }: TeamCardProps) => {
	return (
		<div className="flex flex-1 flex-col rounded-xl bg-[#FAFAFA] px-4 py-3 ring-1 ring-inset ring-[#E9EAEB]">
			<div className="flex flex-col justify-center gap-2">
				<p className="break-words text-lg font-semibold leading-7 text-gray-800">{title}</p>
				<div className="h-px w-full rounded-lg bg-[#E9EAEB]" />
				<div className="flex flex-col gap-0.5">
					<p className="text-sm font-medium leading-5 text-[#181D27]">
						{formatText({ text: 'Total team points:', role: TextRole.InputLabel })} {summary.totalTeamPoints}
					</p>
					{rows.map(({ key, label, override }) => (
						<p key={key} className="text-sm font-medium leading-5 text-[#181D27]">
							{formatText({ text: label, role: TextRole.InputLabel, override })}: {summary[key] ?? 0}
						</p>
					))}
				</div>
			</div>
		</div>
	);
};

export interface PointsSummaryModalProps {
	open: boolean;
	onClose: () => void;
	teamOneTitle: string;
	teamOnePointsSummary?: TeamLeaguePointsSummary | null;
	teamTwoTitle: string;
	teamTwoPointsSummary?: TeamLeaguePointsSummary | null;
	matchupTitle?: string;
	tieBreakerTitle?: string;
	className?: string;
}

export const PointsSummaryModal = ({
	open,
	onClose,
	teamOneTitle,
	teamOnePointsSummary,
	teamTwoTitle,
	teamTwoPointsSummary,
	matchupTitle,
	tieBreakerTitle,
	className
}: PointsSummaryModalProps) => {
	const teamOne = teamOnePointsSummary ?? EMPTY_SUMMARY;
	const teamTwo = teamTwoPointsSummary ?? EMPTY_SUMMARY;
	const matchupLabel = matchupTitle?.trim() || DEFAULT_MATCHUP_TITLE;
	const tieBreakerLabel = tieBreakerTitle?.trim() || DEFAULT_TIE_BREAKER_TITLE;
	const rows = buildComponentRows(matchupLabel, tieBreakerLabel);

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className={cn('rounded-xl border-0 p-4 sm:max-w-[572px] sm:p-6', className)}>
				<div className="flex flex-col gap-3">
					<div className="flex items-center justify-between gap-4">
						<DialogTitle className="text-2xl font-medium leading-8 text-black">
							{formatText({ text: 'Points summary', role: TextRole.ModalTitle })}
						</DialogTitle>
						<DialogClose
							className="shrink-0 rounded-sm opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
							aria-label="Close"
						>
							<FontAwesomeIcon icon={faXmark} className="size-6 text-black" />
						</DialogClose>
					</div>

					<div className="h-px w-full rounded-lg bg-[#E9EAEB]" />

					<DialogSubTitle className="text-xs font-medium leading-[18px] text-gray-900">
						{/* Already in Body case: the league-authored titles are interpolated, so formatText must not re-case them. */}
						{formatText({
							text: `Total team points are the combined points a team earns throughout the season by adding up ${matchupLabel} points, ${tieBreakerLabel} points, Match points, and Game points.`,
							role: TextRole.DescriptiveText,
							override: true
						})}
					</DialogSubTitle>

					<div className="flex flex-col items-stretch gap-3 sm:flex-row">
						<TeamCard title={teamTwoTitle} summary={teamTwo} rows={rows} />
						<TeamCard title={teamOneTitle} summary={teamOne} rows={rows} />
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
